import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { FinancialValue } from '../shared/FinancialValue';

interface BIResultsCardProps {
  result: {
    final_claim_amount: number;
    revenue_loss: number;
    causation_passed: boolean;
    rtp_multiplier?: number;
  };
  // Canonical net from useCanonicalNet (/case-figures → policy_deductions.net_claim).
  // When provided it is the headline figure; result.final_claim_amount is the stale
  // snapshot fallback only (used when /case-figures is unavailable).
  canonicalNet?: number | null;
  isPeakSeason?: boolean;
  contingencyBufferPercent?: number;
  accountantFees?: number;
  continuingExpenses?: number;
  extraExpenses?: number;
}

export const BIResultsCard: React.FC<BIResultsCardProps> = ({
  result,
  canonicalNet,
  isPeakSeason,
  contingencyBufferPercent,
  accountantFees,
  continuingExpenses,
  extraExpenses,
}) => {
  return (
    <div className="rounded-md overflow-hidden mb-6 border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
      <div className="px-6 py-4 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)]">
        <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>BI Calculation Results</h2>
      </div>
      <div className="p-6">
        {/* Peak Season Alert */}
        {isPeakSeason && (
          <div className="mb-4 p-3 rounded-md flex items-center gap-2" style={{ backgroundColor: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <AlertTriangle size={16} style={{ color: 'var(--color-brand)' }} />
            <span className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
              <strong>Peak Season Event:</strong> This claim occurred during a historically high-revenue period.
            </span>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="rounded-md p-4 bg-[var(--color-bg-elevated)]">
            <p className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Final Claim Amount</p>
            <p className="text-2xl font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>
              <FinancialValue value={canonicalNet ?? result.final_claim_amount} size="xl" />
            </p>
            {contingencyBufferPercent && contingencyBufferPercent > 0 && (
              <p className="text-xs mt-1" style={{ color: 'var(--color-verified)' }}>
                Includes {contingencyBufferPercent}% contingency buffer
              </p>
            )}
          </div>
          <div className="rounded-md p-4 bg-[var(--color-bg-elevated)]">
            <p className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Revenue Loss</p>
            <p className="text-xl font-bold font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
              <FinancialValue value={result.revenue_loss} size="lg" />
            </p>
          </div>
          <div className="rounded-md p-4 bg-[var(--color-bg-elevated)]">
            <p className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>Causation Test</p>
            <p className="font-semibold">
              {result.causation_passed ? (
                <span style={{ color: 'var(--color-verified)' }}>✓ Passed</span>
              ) : (
                <span style={{ color: 'var(--color-needs-human-text)' }}>✗ Failed</span>
              )}
            </p>
          </div>
          {result.rtp_multiplier && result.rtp_multiplier !== 1 && (
            <div className="rounded-md p-4 bg-[var(--color-bg-elevated)]">
              <p className="text-sm mb-1" style={{ color: 'var(--color-text-secondary)' }}>RTP Multiplier</p>
              <p className="text-xl font-bold font-mono tabular-nums" style={{ color: 'var(--color-text-primary)' }}>
                {result.rtp_multiplier}x
              </p>
            </div>
          )}
        </div>

        {/* Extra Expenses Summary if present */}
        {((accountantFees && accountantFees > 0) || (continuingExpenses && continuingExpenses > 0) || (extraExpenses && extraExpenses > 0)) && (
          <div className="mt-4 pt-4 border-t border-[var(--color-border)]">
            <p className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Additional Expenses Included:</p>
            <div className="flex flex-wrap gap-3">
              {accountantFees && accountantFees > 0 && (
                <span className="text-xs px-2 py-1 rounded-md bg-[var(--color-bg-elevated)]">
                  Professional Fees: <FinancialValue value={accountantFees} size="sm" />
                </span>
              )}
              {continuingExpenses && continuingExpenses > 0 && (
                <span className="text-xs px-2 py-1 rounded-md bg-[var(--color-bg-elevated)]">
                  Continuing: <FinancialValue value={continuingExpenses} size="sm" />
                </span>
              )}
              {extraExpenses && extraExpenses > 0 && (
                <span className="text-xs px-2 py-1 rounded-md bg-[var(--color-bg-elevated)]">
                  Extra: <FinancialValue value={extraExpenses} size="sm" />
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BIResultsCard;
